// packages/shared/src/services/courtService.ts
import { doc, addDoc, updateDoc, collection, getDoc, getDocs, query, where, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { FirebaseService } from './firebaseService';
import { Court, PaginatedCourtsResult } from '../types';

export class CourtService {
  private static instance: CourtService;
  private firebaseService: FirebaseService;

  private constructor() {
    this.firebaseService = FirebaseService.getInstance();
  }

  public static getInstance(): CourtService {
    if (!CourtService.instance) {
      CourtService.instance = new CourtService();
    }
    return CourtService.instance;
  }

  public async createCourt(courtData: Omit<Court, 'id'>): Promise<Court> {
    const db = this.firebaseService.getFirestore();
    const docRef = await addDoc(collection(db, 'courts'), courtData);
    return { id: docRef.id, ...courtData };
  }

  public async updateCourt(id: string, updates: Partial<Court>): Promise<void> {
    const db = this.firebaseService.getFirestore();
    await updateDoc(doc(db, 'courts', id), {
      ...updates,
      updatedAt: new Date()
    });
  }

  public async getCourt(id: string): Promise<Court | null> {
    const db = this.firebaseService.getFirestore();
    const courtDoc = await getDoc(doc(db, 'courts', id));

    if (!courtDoc.exists()) {
      return null;
    }

    const data = courtDoc.data();
    return { 
      id: courtDoc.id, 
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Court;
  }

  public async getAllCourts(): Promise<Court[]> {
    const db = this.firebaseService.getFirestore();
    
    // For large datasets, use pagination internally
    const courts: Court[] = [];
    let lastDoc: DocumentSnapshot | null = null;
    const batchSize = 1000;
    
    do {
      let q = query(
        collection(db, 'courts'),
        orderBy('name'),
        limit(batchSize)
      );
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const querySnapshot = await getDocs(q);
      
      const batchCourts = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }) as Court[];
      
      courts.push(...batchCourts);
      
      if (querySnapshot.docs.length < batchSize) {
        break;
      }
      
      lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    } while (true);
    
    return courts;
  }

  /**
   * Get courts with filtering options
   */
  public async getFilteredCourts(filters: {
    city?: string;
    country?: string;
    facilityType?: string;
    courtType?: string;
    lights?: boolean;
    indoorCourts?: boolean;
    limitCount?: number;
  }, pageSize: number = 25, pageToken?: string): Promise<PaginatedCourtsResult> {
    const db = this.firebaseService.getFirestore();
    let q = query(collection(db, 'courts'));

    // Apply filters
    if (filters.city) {
      q = query(q, where('location.city', '==', filters.city));
    }
    
    if (filters.country) {
      q = query(q, where('location.country', '==', filters.country));
    }
    
    if (filters.facilityType) {
      q = query(q, where('details.facility_type', '==', filters.facilityType));
    }
    
    if (filters.lights !== undefined) {
      q = query(q, where('details.lights', '==', filters.lights));
    }
    
    if (filters.indoorCourts) {
      q = query(q, where('details.indoor_courts', '>', 0));
    }

    // Add ordering and limit
    q = query(q, orderBy('name'));
    
    // Handle pagination
    if (pageToken) {
      const startAfterDoc = await getDoc(doc(db, 'courts', pageToken));
      if (startAfterDoc.exists()) {
        q = query(q, startAfter(startAfterDoc));
      }
    }
    
    q = query(q, limit(pageSize + 1)); // Get one extra to check if there's a next page

    const querySnapshot = await getDocs(q);
    const docs = querySnapshot.docs;
    
    const hasNextPage = docs.length > pageSize;
    const courtsToReturn = hasNextPage ? docs.slice(0, -1) : docs;
    
    const courts = courtsToReturn.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    }) as Court[];

    return {
      courts,
      hasNextPage,
      hasPrevPage: !!pageToken,
      nextPageToken: hasNextPage ? courtsToReturn[courtsToReturn.length - 1].id : undefined
    };
  }

  /**
   * Search courts by name
   */
  public async searchCourtsByName(searchTerm: string, pageSize: number = 25): Promise<Court[]> {
    const db = this.firebaseService.getFirestore();
    
    // For search, limit to prevent overwhelming the emulator
    const q = query(
      collection(db, 'courts'),
      orderBy('name'),
      limit(pageSize * 5) // Get more for client-side filtering
    );
    
    const querySnapshot = await getDocs(q);
    const allCourts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    }) as Court[];
    
    return allCourts
      .filter(court => 
        court.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        court.location.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        court.location.address.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, pageSize);
  }

  /**
   * Get unique cities from courts - use aggregation for large datasets
   */
  public async getUniqueCities(): Promise<string[]> {
    const db = this.firebaseService.getFirestore();
    
    // For large datasets, sample rather than getting all
    const q = query(
      collection(db, 'courts'),
      orderBy('location.city'),
      limit(1000)
    );
    
    const querySnapshot = await getDocs(q);
    const cities = new Set<string>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.location?.city) {
        cities.add(data.location.city);
      }
    });
    
    return Array.from(cities).sort();
  }

  /**
   * Get unique countries from courts
   */
  public async getUniqueCountries(): Promise<string[]> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'courts'),
      orderBy('location.country'),
      limit(1000)
    );
    
    const querySnapshot = await getDocs(q);
    const countries = new Set<string>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.location?.country) {
        countries.add(data.location.country);
      }
    });
    
    return Array.from(countries).sort();
  }

  /**
   * Get unique facility types from courts
   */
  public async getUniqueFacilityTypes(): Promise<string[]> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'courts'),
      limit(1000)
    );
    
    const querySnapshot = await getDocs(q);
    const types = new Set<string>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.details?.facility_type) {
        types.add(data.details.facility_type);
      }
    });
    
    return Array.from(types).sort();
  }

  /**
   * Get unique court surfaces from courts
   */
  public async getUniqueCourtSurfaces(): Promise<string[]> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'courts'),
      limit(1000)
    );
    
    const querySnapshot = await getDocs(q);
    const surfaces = new Set<string>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.details?.court_surface) {
        surfaces.add(data.details.court_surface);
      }
    });
    
    return Array.from(surfaces).sort();
  }

  /**
   * Get unique court fees from courts
   */
  public async getUniqueCourtFees(): Promise<string[]> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'courts'),
      limit(1000)
    );
    
    const querySnapshot = await getDocs(q);
    const fees = new Set<string>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.pricing?.hourly !== undefined) {
        fees.add(data.pricing.hourly.toString());
      }
    });
    
    return Array.from(fees).sort();
  }

  public async findNearByCourts(
    latitude: number,
    longitude: number,
    radiusMiles: number = 10
  ): Promise<Court[]> {
    // For large datasets, this should use geospatial queries
    // For now, limit the search scope
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'courts'),
      limit(5000) // Limit to prevent overwhelming emulator
    );
    
    const querySnapshot = await getDocs(q);
    const courts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    }) as Court[];
    
    return courts.filter(court => {
      const distance = this.calculateDistance(
        latitude,
        longitude,
        court.location.coordinates[1], // latitude is at index 1
        court.location.coordinates[0]  // longitude is at index 0
      );
      return distance <= radiusMiles;
    });
  }

private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth's radius in miles (was 6371 for km)
  const dLat = this.toRad(lat2 - lat1);
  const dLon = this.toRad(lon2 - lon1);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}